import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { insuranceAPI } from '../../lib/insuranceAPI';

const EMPTY = { policy_type: 'professional_indemnity', custom_label: '', insurer: '', policy_number: '', cover_amount: '', excess: '', start_date: '', expiry_date: '', notes: '' };
const err = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg?.replace('Value error, ', '')).join('; ') : d || 'Something went wrong';
};

// mode: 'add' | 'edit' | 'renew'
export const PolicyDialog = ({ mode, policy, onClose, onSaved }) => {
  const base = policy ? { ...EMPTY, ...policy, custom_label: policy.custom_label || '', excess: policy.excess ?? '', notes: policy.notes || '' } : EMPTY;
  const [f, setF] = useState(mode === 'renew' ? { ...base, policy_number: '', start_date: policy.expiry_date, expiry_date: '' } : base);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setBusy(true);
    const body = { ...f, cover_amount: Number(f.cover_amount || 0), excess: f.excess === '' ? null : Number(f.excess), custom_label: f.custom_label || null, notes: f.notes || null };
    try {
      const p = mode === 'edit' ? await insuranceAPI.update(policy.id, body) : mode === 'renew' ? await insuranceAPI.renew(policy.id, body) : await insuranceAPI.create(body);
      if (files.length) {
        try {
          const r = await insuranceAPI.upload(p.id, files);
          if (r.rejected?.length) toast.warning(r.rejected.map((x) => `${x.filename}: ${x.reason}`).join('; '));
        } catch (e) { toast.error(`Policy saved, but the certificate upload failed: ${err(e)}`); }
      }
      toast.success(mode === 'renew' ? 'Policy renewed' : 'Policy saved');
      onSaved();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="ins-policy-dialog">
        <DialogHeader><DialogTitle>{mode === 'edit' ? 'Edit policy' : mode === 'renew' ? 'Renew policy' : 'Add policy'}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label className="text-xs">Type</Label>
            <Select value={f.policy_type} onValueChange={set('policy_type')} disabled={mode === 'renew'}>
              <SelectTrigger data-testid="ins-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="professional_indemnity">Professional indemnity</SelectItem>
                <SelectItem value="public_liability">Public liability</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select></div>
          {f.policy_type === 'other' && <div className="space-y-1"><Label className="text-xs">Label</Label><Input placeholder="e.g. Cyber" value={f.custom_label} onChange={set('custom_label')} data-testid="ins-custom-label" /></div>}
          <div className="space-y-1"><Label className="text-xs">Insurer</Label><Input value={f.insurer} onChange={set('insurer')} data-testid="ins-insurer" /></div>
          <div className="space-y-1"><Label className="text-xs">Policy number</Label><Input value={f.policy_number} onChange={set('policy_number')} data-testid="ins-policy-number" /></div>
          <div className="space-y-1"><Label className="text-xs">Cover amount (AUD)</Label><Input type="number" min="0" value={f.cover_amount} onChange={set('cover_amount')} data-testid="ins-cover" /></div>
          <div className="space-y-1"><Label className="text-xs">Excess (AUD, optional)</Label><Input type="number" min="0" value={f.excess} onChange={set('excess')} data-testid="ins-excess" /></div>
          <div className="space-y-1"><Label className="text-xs">Start date</Label><Input type="date" value={f.start_date} onChange={set('start_date')} data-testid="ins-start" /></div>
          <div className="space-y-1"><Label className="text-xs">Expiry date</Label><Input type="date" value={f.expiry_date} onChange={set('expiry_date')} data-testid="ins-expiry" /></div>
          <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Notes</Label><Textarea value={f.notes} onChange={set('notes')} className="min-h-[60px]" data-testid="ins-notes" /></div>
          <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Certificate of currency (PDF, image or Word; 25 MB max)</Label>
            <Input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="ins-files" /></div>
        </div>
        <Button onClick={save} disabled={busy || !f.insurer || !f.policy_number || !f.start_date || !f.expiry_date} data-testid="ins-save">{busy ? 'Saving…' : 'Save'}</Button>
      </DialogContent>
    </Dialog>
  );
};
